/**
 * Lightweight API client using fetch.
 * - All URLs from REACT_APP_BACKEND_URL (AR-12).
 * - Access token in memory; refresh token transport supports cookie OR JSON body (AR-9).
 */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/v1`;

let accessToken = null;
let refreshToken = null;
let onAuthChange = null;

export function getAccessToken() { return accessToken; }
export function getRefreshToken() { return refreshToken; }

export function setTokens({ access_token, refresh_token }) {
  accessToken = access_token || null;
  if (refresh_token !== undefined) refreshToken = refresh_token || null;
  if (accessToken) localStorage.setItem("avcp_at", accessToken);
  else localStorage.removeItem("avcp_at");
  if (refreshToken) localStorage.setItem("avcp_rt", refreshToken);
  else localStorage.removeItem("avcp_rt");
  if (onAuthChange) onAuthChange();
}

export function clearTokens() {
  accessToken = null;
  refreshToken = null;
  localStorage.removeItem("avcp_at");
  localStorage.removeItem("avcp_rt");
  if (onAuthChange) onAuthChange();
}

export function bootstrapFromStorage() {
  accessToken = localStorage.getItem("avcp_at") || null;
  refreshToken = localStorage.getItem("avcp_rt") || null;
}

export function setOnAuthChange(fn) { onAuthChange = fn; }

async function rawFetch(path, opts = {}) {
  const url = `${BASE}${path}`;
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (accessToken && !headers.Authorization) headers.Authorization = `Bearer ${accessToken}`;
  const resp = await fetch(url, {
    method: opts.method || "GET",
    headers,
    credentials: "include",
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  });
  return resp;
}

async function tryRefresh() {
  if (!refreshToken) return false;
  const resp = await fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Refresh ${refreshToken}`,
    },
    credentials: "include",
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  if (!resp.ok) return false;
  const data = await resp.json();
  setTokens(data);
  return true;
}

export async function api(path, opts = {}) {
  let resp = await rawFetch(path, opts);
  if (resp.status === 401 && refreshToken && !opts._retried) {
    const ok = await tryRefresh();
    if (ok) return api(path, { ...opts, _retried: true });
    clearTokens();
  }
  let data = null;
  const text = await resp.text();
  if (text) {
    try { data = JSON.parse(text); } catch { data = { raw: text }; }
  }
  if (!resp.ok) {
    const err = new Error(data?.detail || data?.title || `Request failed (${resp.status})`);
    err.status = resp.status;
    err.problem = data;
    throw err;
  }
  return data;
}

export async function apiUpload(path, file, fieldName = "file") {
  const url = `${BASE}${path}`;
  const fd = new FormData();
  fd.append(fieldName, file);
  const headers = {};
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const resp = await fetch(url, {
    method: "POST",
    headers,
    credentials: "include",
    body: fd,
  });
  const text = await resp.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }
  if (!resp.ok) {
    const err = new Error(data?.title || data?.detail || `Upload failed (${resp.status})`);
    err.status = resp.status;
    err.problem = data;
    throw err;
  }
  return data;
}
